// Toast.js
import React, { useEffect, useCallback } from 'react'

/**
 * Toast types: 'success' | 'error' | 'warning' | 'info'
 *
 * Usage:
 * import { useToast, ToastContainer } from './Toast'
 *
 * // In your root component (e.g. App.js):
 * const { toasts, showToast, removeToast } = useToast()
 * <ToastContainer toasts={toasts} removeToast={removeToast} />
 *
 * // Anywhere you need a toast:
 * showToast('Saved successfully!', 'success')
 * showToast('Something went wrong.', 'error')
 * showToast('Please check your input.', 'warning')
 * showToast('New update available.', 'info')
 */

const ICONS = {
    success: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    ),
    error: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    ),
    warning: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    info: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    ),
}

const COLORS = {
    success: { bg: '#f0fdf4', border: '#4ebfce', icon: '#5a5ce9', text: '#1d0955' },
    error: { bg: '#fff1f2', border: '#fca5a5', icon: '#dc2626', text: '#b91c1c' },
    warning: { bg: '#fffbeb', border: '#fcd34d', icon: '#d97706', text: '#b45309' },
    info: { bg: '#eff6ff', border: '#93c5fd', icon: '#2563eb', text: '#1d4ed8' },
}

// Single Toast item
const Toast = ({ id, message, type = 'info', removeToast }) => {
    const colors = COLORS[type] || COLORS.info

    useEffect(() => {
        const timer = setTimeout(() => removeToast(id), 4000)
        return () => clearTimeout(timer)
    }, [id, removeToast])

    return (
        <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '12px 14px',
                borderRadius: '10px',
                border: `1px solid ${colors.border}`,
                background: colors.bg,
                boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                minWidth: '280px',
                maxWidth: '380px',
                animation: 'toastSlideIn 0.25s cubic-bezier(0.4,0,0.2,1)',
                position: 'relative',
            }}
        >
            {/* Icon */}
            <span style={{ color: colors.icon, flexShrink: 0, marginTop: '1px' }}>
                {ICONS[type]}
            </span>

            {/* Message */}
            <span style={{
                flex: 1,
                fontSize: '14px',
                fontWeight: 500,
                color: colors.text,
                lineHeight: 1.4,
            }}>
                {message}
            </span>

            {/* Close button */}
            <button
                onClick={() => removeToast(id)}
                aria-label="Dismiss notification"
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: colors.icon,
                    padding: '0',
                    lineHeight: 1,
                    opacity: 0.7,
                    fontSize: '16px',
                    flexShrink: 0,
                }}
            >
                ✕
            </button>

            {/* Progress bar */}
            <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                height: '3px',
                borderRadius: '0 0 10px 10px',
                background: colors.icon,
                animation: 'toastProgress 4s linear forwards',
                width: '100%',
            }} />
        </div>
    )
}

// Container that renders all toasts
export const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <>
            <style>{`
                @keyframes toastSlideIn {
                    from { opacity: 0; transform: translateX(40px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes toastProgress {
                    from { width: 100%; }
                    to   { width: 0%; }
                }
            `}</style>
            <div
                aria-label="Notifications"
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    zIndex: 9999,
                }}
            >
                {toasts.map((toast) => (
                    <Toast
                        key={toast.id}
                        {...toast}
                        removeToast={removeToast}
                    />
                ))}
            </div>
        </>
    )
}

// Hook to manage toasts
export const useToast = () => {
    const [toasts, setToasts] = React.useState([])

    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now() + Math.random()
        setToasts((prev) => [...prev, { id, message, type }])
    }, [])

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
    }, [])

    return { toasts, showToast, removeToast }
}

export default Toast